import Container, { Column, Row } from '@/components/container/Container';
import {
	TextBodySmall,
	TextHeadlineMedium,
	TextLabelLarge2,
	TextTitleMedium2,
} from '@/components/text/Texts';
import { Button } from 'primereact/button';
import { type Resolver, useForm } from 'react-hook-form';
import { MdArrowBack, MdArrowForward, MdClose } from 'react-icons/md';
import '../views/concierge.styles.css';
import './new-send-booking.css';
import { PhoneHelper } from '@/common/PhoneHelper';
import { SelectCountryIconDropdownConcierge } from '@/components/form-inputs/select-country/SelectCountryIconDropdown';
import { InputController } from '@/components/inputs/InputController';
import TextInput from '@/components/inputs/TextInput';
import Spinner from '@/components/utils/Spinner';
import { cn } from '@/components/ui/cn';
import { ENDPOINTS } from '@/constants/endpoints';
import { routeWithId } from '@/constants/routes';
import { VALIDATION_MESSAGES } from '@/constants/validation';
import FlagsModel, {
	type Flag,
} from '@/modules/authentication/sign-up/components/CountryFlags';
import { ConciergeLiveMap } from '@/modules/concierge/map/ConciergeLiveMap';
import AddressDropdownInput, {
	X4bPointType,
} from '@/modules/concierge/new-ride/AddressDropdownInput';
import ConciergeHeader from '@/modules/concierge/views/ConciergeHeader';
import type { X4bLocation } from '@/modules/concierge/views/PlacesRepository';
import type { GoogleMapsRoutingResponse } from '@/modules/map/engine/GoogleTypes';
import {
	X4B_GET_SERVICES_NO_VEHICLES_MESSAGE,
	XpressServiceType,
	BookingType,
	type GetServicesAndPricesRequest,
	type GetServicesAndPricesResponse,
	type PlaceOrderRequest,
	type VehicleTypeDto,
	type X4bActivityPointDto,
} from '@/modules/vehicle/engine/ActivityTypes';
import {
	vehicleTypes,
	XpressVehicleType,
} from '@/modules/vehicle/engine/VehicleType';
import { axiosInstance } from '@/services/axios.service';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import type { DropdownChangeEvent } from 'primereact/dropdown';
import { showNotification } from '@/modules/notification/engine/notification.library';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import * as yup from 'yup';
import { string } from 'yup';
import NewSendBookingStep2 from './NewSendBookingStep2';
import NewSendBookingStep3 from './NewSendBookingStep3';
import {
	firstActiveVehicleType,
	readVehicleTypeIsActive,
	SectionHeading,
} from './NewSendBookingHelpers';
import {
	DELIVERY_WEIGHT_MAX_KG_OPTIONS,
	type NewSendBookingFormValues,
	type ReturnSendBookingType,
	DeliveryItemType,
	DeliverySize,
} from './formTypes';

export {
	DELIVERY_PAYERS_SELECTABLE,
	DELIVERY_WEIGHT_MAX_KG_OPTIONS,
	DeliveryItemType,
	DeliverySize,
	isDeliveryPayerSelectable,
	type DeliveryPayerOption,
} from './formTypes';

const formId = 'newSendBookingStep1';

function splitContactName(full: string): {
	FirstName: string;
	LastName: string;
} {
	const t = full.trim();
	if (!t) return { FirstName: '', LastName: '' };
	const idx = t.indexOf(' ');
	if (idx === -1) return { FirstName: t, LastName: '' };
	return {
		FirstName: t.slice(0, idx).trim(),
		LastName: t.slice(idx + 1).trim(),
	};
}

/**
 * X4B `AzureBlobHelper` / `IsBase64Encoded` expect a full data URL:
 * `data:<mime>;base64,<payload>` (same regex as server), not raw base64 only.
 */
async function readParcelPhotoAsDataUrlForPlaceOrder(
	file: File,
): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const data = reader.result as string;
			if (typeof data !== 'string' || !/^data:.+;base64,/.test(data)) {
				reject(
					new Error(
						'Parcel photo must be a data URL from readAsDataURL',
					),
				);
				return;
			}
			resolve(data);
		};
		reader.onerror = () =>
			reject(reader.error ?? new Error('Failed to read parcel photo'));
		reader.readAsDataURL(file);
	});
}

function resolveDeliveryVehicleType(
	types: VehicleTypeDto[],
	vehicleTypeId: XpressVehicleType,
): VehicleTypeDto | undefined {
	if (!types.length) return undefined;
	const firstActive = firstActiveVehicleType(types);
	if (!firstActive) return undefined;
	const match = types.find((t) => t.VehicleTypeId === vehicleTypeId);
	if (match && readVehicleTypeIsActive(match)) return match;
	return firstActive;
}

function hasSelectableDeliveryVehicle(types: VehicleTypeDto[] | undefined) {
	return !!types?.some((t) => readVehicleTypeIsActive(t));
}

export type {
	NewSendBookingFormValues,
	NewSendBookingStep1Values,
} from './formTypes';

const phoneSchema = (
	prefixField: 'senderMobileNumberPrefix' | 'recipientMobileNumberPrefix',
) =>
	string()
		.trim()
		.required(VALIDATION_MESSAGES.required)
		.test(
			'is-valid-phone',
			'Please enter a valid phone number',
			function (value) {
				if (!value) return true;
				try {
					const countryCode = ((
						this.parent as NewSendBookingFormValues
					)[prefixField] || 'PH') as CountryCode;
					const prepared = PhoneHelper.preparePhoneNumber(
						value,
						countryCode,
					);
					return isValidPhoneNumber(prepared, countryCode);
				} catch {
					return false;
				}
			},
		);

const step1Schema = yup.object({
	senderContactName: yup
		.string()
		.trim()
		.required(VALIDATION_MESSAGES.required),
	senderPhone: phoneSchema('senderMobileNumberPrefix'),
	senderMobileNumberPrefix: yup.string().trim().default('PH'),
	pickUpAddress: yup
		.mixed<X4bLocation>()
		.required(VALIDATION_MESSAGES.required),
	recipientContactName: yup
		.string()
		.trim()
		.required(VALIDATION_MESSAGES.required),
	recipientPhone: phoneSchema('recipientMobileNumberPrefix'),
	recipientMobileNumberPrefix: yup.string().trim().default('PH'),
	dropOffAddress: yup
		.mixed<X4bLocation>()
		.required(VALIDATION_MESSAGES.required),
});

const step2Schema = yup.object({
	deliveryItemType: yup
		.number()
		.oneOf(
			[
				DeliveryItemType.Document,
				DeliveryItemType.Food,
				DeliveryItemType.Clothing,
				DeliveryItemType.Electronic,
				DeliveryItemType.Fragile,
				DeliveryItemType.Medical,
				DeliveryItemType.Other,
			],
			'Select an item type',
		)
		.required(VALIDATION_MESSAGES.required),
	parcelSize: yup
		.number()
		.oneOf(
			[DeliverySize.Small, DeliverySize.Medium],
			VALIDATION_MESSAGES.required,
		)
		.required(VALIDATION_MESSAGES.required),
	weightMaxKg: yup
		.number()
		.oneOf(
			[...DELIVERY_WEIGHT_MAX_KG_OPTIONS],
			VALIDATION_MESSAGES.required,
		)
		.required(VALIDATION_MESSAGES.required),
	driverNotes: yup.string().trim().optional(),
	payer: yup
		.string()
		.oneOf(
			['x4b'],
			'Cash payment for delivery is not available yet. Use X4B Program (company billed).',
		)
		.required(VALIDATION_MESSAGES.required),
});

function defaultPhFlag(): Flag {
	const flag = FlagsModel.flagByCountryCode('PH');
	if (flag) {
		return flag;
	}
	const fallback = FlagsModel.countries.find((c) => c.code === 'PH');
	if (fallback) {
		return fallback;
	}
	return FlagsModel.countries[0];
}

const NewSendBooking = () => {
	const navigate = useNavigate();
	const location = useLocation();
	// Prefill payload for "Book return" coming from a completed delivery order.
	const returnBooking = (
		location.state as { returnSendBooking?: ReturnSendBookingType } | null
	)?.returnSendBooking;

	const [step, setStep] = useState<1 | 2 | 3>(1);
	const [senderCountry, setSenderCountry] = useState<Flag>(() =>
		returnBooking?.senderMobileNumberPrefix
			? FlagsModel.flagByCountryCode(
					returnBooking.senderMobileNumberPrefix,
				) ?? defaultPhFlag()
			: defaultPhFlag(),
	);
	const [recipientCountry, setRecipientCountry] = useState<Flag>(() =>
		returnBooking?.recipientMobileNumberPrefix
			? FlagsModel.flagByCountryCode(
					returnBooking.recipientMobileNumberPrefix,
				) ?? defaultPhFlag()
			: defaultPhFlag(),
	);
	const [googleMapsRoute, setGoogleMapsRoute] =
		useState<GoogleMapsRoutingResponse | null>(null);
	const [parcelPhoto, setParcelPhoto] = useState<File | null>(null);
	/** Single blob URL for the current file — owned here so Step 2 ↔ Step 3 navigation does not revoke a URL still in use. */
	const [parcelPhotoPreviewUrl, setParcelPhotoPreviewUrl] = useState<
		string | null
	>(null);

	useLayoutEffect(() => {
		if (!parcelPhoto) {
			setParcelPhotoPreviewUrl(null);
			return;
		}
		const url = URL.createObjectURL(parcelPhoto);
		setParcelPhotoPreviewUrl(url);
		return () => {
			URL.revokeObjectURL(url);
		};
	}, [parcelPhoto]);

	const [deliveryVehicleTypeId, setDeliveryVehicleTypeId] =
		useState<XpressVehicleType>(
			returnBooking?.vehicleType ??
				vehicleTypes[1]?.xpressVehicleType ??
				XpressVehicleType.Sedan,
		);
	const [step2Errors, setStep2Errors] = useState<
		Partial<
			Record<
				| 'deliveryItemType'
				| 'parcelSize'
				| 'weightMaxKg'
				| 'parcelPhoto'
				| 'payer',
				string
			>
		>
	>({});

	const form = useForm<NewSendBookingFormValues>({
		defaultValues: {
			senderContactName: returnBooking?.senderContactName ?? '',
			senderPhone: returnBooking?.senderPhone ?? '',
			senderMobileNumberPrefix: senderCountry.code,
			pickUpAddress: returnBooking?.pickUpAddress ?? undefined,
			recipientContactName: returnBooking?.recipientContactName ?? '',
			recipientPhone: returnBooking?.recipientPhone ?? '',
			recipientMobileNumberPrefix: recipientCountry.code,
			dropOffAddress: returnBooking?.dropOffAddress ?? undefined,
			deliveryItemType:
				returnBooking?.deliveryItemType ?? DeliveryItemType.None,
			parcelSize: returnBooking?.parcelSize ?? DeliverySize.None,
			weightMaxKg: returnBooking?.weightMaxKg ?? 0,
			driverNotes: returnBooking?.driverNotes ?? '',
			payer: 'x4b',
		},
		resolver: yupResolver(
			step1Schema,
		) as unknown as Resolver<NewSendBookingFormValues>,
		mode: 'all',
	});

	// When prefilled from a return booking, validate immediately so the user can
	// proceed past step 1 without re-touching every field.
	useEffect(() => {
		if (returnBooking) {
			void form.trigger();
		}
		// Only run once on mount for the incoming return-booking payload.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const values = form.watch();

	const buildPointsForRequest = useCallback(() => {
		return [values.pickUpAddress, values.dropOffAddress]
			.filter((i): i is X4bLocation => !!(i?.Latitude && i?.Longitude))
			.map(
				(point, ind) =>
					({
						PlaceName: point?.PlaceName,
						Address: point?.Address,
						Latitude: point?.Latitude,
						Longitude: point?.Longitude,
						SortOrder: ind,
						Name: point?.Name,
					}) as X4bActivityPointDto,
			);
	}, [values.pickUpAddress, values.dropOffAddress]);

	const routeGeoReady = useMemo(
		() =>
			!!(
				values.pickUpAddress?.Latitude &&
				values.pickUpAddress?.Longitude &&
				values.dropOffAddress?.Latitude &&
				values.dropOffAddress?.Longitude
			),
		[
			values.pickUpAddress?.Latitude,
			values.pickUpAddress?.Longitude,
			values.dropOffAddress?.Latitude,
			values.dropOffAddress?.Longitude,
		],
	);

	const {
		data: routePricingData,
		isFetching: routePricingLoading,
		isPending: routePricingPending,
		isError: routePricingIsError,
		error: routePricingQueryError,
	} = useQuery({
		queryKey: [
			'NewSendBooking.ServicesAndPrices',
			XpressServiceType.Delivery,
			values.pickUpAddress,
			values.dropOffAddress,
		],
		queryFn: async () => {
			const pointsForRequest = buildPointsForRequest();
			if (pointsForRequest.length < 2) {
				throw new Error(
					'Both pickup and dropoff addresses are required',
				);
			}
			const result =
				await axiosInstance.post<GetServicesAndPricesResponse>(
					ENDPOINTS['/api/X4bActivity/GetServicesAndPrices'],
					{
						Points: pointsForRequest,
						ServiceTypeId: XpressServiceType.Delivery,
					} satisfies GetServicesAndPricesRequest,
				);

			if (!(result.data.VehicleTypes?.length)) {
				throw new Error(X4B_GET_SERVICES_NO_VEHICLES_MESSAGE);
			}

			if (result.data.GoogleMapsRouting) {
				try {
					setGoogleMapsRoute(
						JSON.parse(
							result.data.GoogleMapsRouting,
						) as GoogleMapsRoutingResponse,
					);
				} catch {
					setGoogleMapsRoute(null);
				}
			} else {
				setGoogleMapsRoute(null);
			}
			return result.data;
		},
		enabled: routeGeoReady,
		refetchOnWindowFocus: false,
	});

	const selectedDeliveryPricingVehicle = useMemo(() => {
		const types = routePricingData?.VehicleTypes;
		if (!types?.length) return undefined;
		return resolveDeliveryVehicleType(types, deliveryVehicleTypeId);
	}, [routePricingData, deliveryVehicleTypeId]);

	useEffect(() => {
		const types = routePricingData?.VehicleTypes;
		if (!types?.length) return;
		setDeliveryVehicleTypeId((current) => {
			const cur = types.find((t) => t.VehicleTypeId === current);
			if (cur && readVehicleTypeIsActive(cur)) return current;
			return (firstActiveVehicleType(types) ?? types[0])!
				.VehicleTypeId;
		});
	}, [routePricingData?.VehicleTypes]);

	const routePricingQueryErrorMessage =
		routePricingQueryError instanceof Error
			? routePricingQueryError.message
			: X4B_GET_SERVICES_NO_VEHICLES_MESSAGE;

	const step1NextDisabled = useMemo(() => {
		if (!form.formState.isValid) return true;
		if (!routeGeoReady) return true;
		return (
			routePricingPending ||
			routePricingLoading ||
			routePricingIsError ||
			!(routePricingData?.VehicleTypes?.length ?? 0)
		);
	}, [
		form.formState.isValid,
		routeGeoReady,
		routePricingPending,
		routePricingLoading,
		routePricingIsError,
		routePricingData?.VehicleTypes?.length,
	]);

	const pickupLabel = useMemo(
		() =>
			values.pickUpAddress?.PlaceName ||
			values.pickUpAddress?.Address ||
			'',
		[values.pickUpAddress],
	);
	const dropoffLabel = useMemo(
		() =>
			values.dropOffAddress?.PlaceName ||
			values.dropOffAddress?.Address ||
			'',
		[values.dropOffAddress],
	);

	const step2Complete = useMemo(() => {
		if (
			values.deliveryItemType === DeliveryItemType.None ||
			values.deliveryItemType === undefined
		) {
			return false;
		}
		if (
			values.parcelSize === DeliverySize.None ||
			values.parcelSize === undefined
		) {
			return false;
		}
		if (values.weightMaxKg === 0 || values.weightMaxKg === undefined) {
			return false;
		}
		if (!values.payer || values.payer !== 'x4b') {
			return false;
		}
		if (!parcelPhoto) return false;
		if (parcelPhoto.size > 10 * 1024 * 1024) return false;
		if (!/^image\/(jpeg|png)$/i.test(parcelPhoto.type)) return false;
		if (!(routePricingData?.VehicleTypes?.length ?? 0)) return false;
		if (!hasSelectableDeliveryVehicle(routePricingData?.VehicleTypes)) {
			return false;
		}
		if (routePricingIsError) return false;
		return true;
	}, [
		values.deliveryItemType,
		values.parcelSize,
		values.weightMaxKg,
		values.payer,
		parcelPhoto,
		routePricingData?.VehicleTypes,
		routePricingIsError,
	]);

	const senderSubtitle = useMemo(() => {
		const name = values.senderContactName?.trim() || 'Sender';
		const phone = values.senderPhone?.trim() || '';
		return phone ? `${name} • ${phone}` : name;
	}, [values.senderContactName, values.senderPhone]);

	const recipientSubtitle = useMemo(() => {
		const name = values.recipientContactName?.trim() || 'Recipient';
		const phone = values.recipientPhone?.trim() || '';
		return phone ? `${name} • ${phone}` : name;
	}, [values.recipientContactName, values.recipientPhone]);

	const goNextFromStep1 = async () => {
		const ok = await form.trigger();
		if (ok) {
			form.setValue('payer', 'x4b', {
				shouldValidate: true,
			});
			setStep2Errors({});
			setStep(2);
		}
	};

	const goNextFromStep2 = () => {
		const v = form.getValues();
		const next: Partial<
			Record<
				| 'deliveryItemType'
				| 'parcelSize'
				| 'weightMaxKg'
				| 'parcelPhoto'
				| 'payer',
				string
			>
		> = {};
		try {
			step2Schema.validateSync(
				{
					deliveryItemType: v.deliveryItemType,
					parcelSize: v.parcelSize,
					weightMaxKg: v.weightMaxKg,
					driverNotes: v.driverNotes || '',
					payer: v.payer,
				},
				{ abortEarly: false },
			);
		} catch (e) {
			if (e instanceof yup.ValidationError && e.inner) {
				for (const err of e.inner) {
					const p = err.path as
						| 'deliveryItemType'
						| 'parcelSize'
						| 'weightMaxKg'
						| 'payer'
						| undefined;
					if (p) next[p] = err.message;
				}
			}
		}
		if (!parcelPhoto) {
			next.parcelPhoto = 'A parcel photo is required';
		} else if (parcelPhoto.size > 10 * 1024 * 1024) {
			next.parcelPhoto = 'File must be 10MB or less';
		} else if (!/^image\/(jpeg|png)$/i.test(parcelPhoto.type)) {
			next.parcelPhoto = 'Use JPEG or PNG only';
		}
		setStep2Errors(next);
		if (Object.keys(next).length > 0) return;
		setStep2Errors({});
		setStep(3);
	};

	const placeOrderMutation = useMutation({
		mutationFn: async () => {
			const v = form.getValues();
			const photo = parcelPhoto;
			if (!photo) {
				throw new Error('Parcel photo is required');
			}
			const pricing = routePricingData;
			const types = pricing?.VehicleTypes;
			if (!types?.length) {
				throw new Error('Pricing is not available');
			}
			const vehicle = resolveDeliveryVehicleType(
				types,
				deliveryVehicleTypeId,
			);
			if (!vehicle) {
				throw new Error('No bookable vehicle is available');
			}
			const points = [v.pickUpAddress, v.dropOffAddress]
				.filter((i): i is X4bLocation => !!(i?.Latitude && i?.Longitude))
				.map(
					(point, ind) =>
						({
							PlaceName: point?.PlaceName,
							Address: point?.Address,
							Latitude: point?.Latitude,
							Longitude: point?.Longitude,
							SortOrder: ind,
							Name: point?.Name,
						}) as X4bActivityPointDto,
				);
			if (points.length < 2) {
				throw new Error('Pickup and drop-off are required');
			}

			const senderPhone = PhoneHelper.preparePhoneNumber(
				v.senderPhone,
				v.senderMobileNumberPrefix || 'PH',
			);
			const recipientPhone = PhoneHelper.preparePhoneNumber(
				v.recipientPhone,
				v.recipientMobileNumberPrefix || 'PH',
			);
			const senderNm = splitContactName(v.senderContactName);
			const recipientNm = splitContactName(v.recipientContactName);
			const notes = (v.driverNotes || '').trim();
			const packagePhotoDataUrl =
				await readParcelPhotoAsDataUrlForPlaceOrder(photo);

			const additionalPassengers =
				senderPhone === recipientPhone
					? []
					: [
							{
								FirstName: recipientNm.FirstName,
								LastName: recipientNm.LastName,
								Phone: recipientPhone,
							},
						];

			const body: PlaceOrderRequest = {
				XpressId: uuidv4(),
				FareId: vehicle.FareId,
				Amount: Number(vehicle.Amount),
				VehicleType: vehicle.VehicleTypeId,
				BookingType: BookingType.Asap,
				TimeZoneOffset: moment().utcOffset() / 60,
				Note: notes || undefined,
				Points: points,
				HeadPassenger: {
					FirstName: senderNm.FirstName,
					LastName: senderNm.LastName,
					Phone: senderPhone,
				},
				AdditionalPassengers: additionalPassengers,
				GoogleMapsRouting: pricing?.GoogleMapsRouting ?? undefined,
				DeliveryOptions: {
					DeliveryRecipientName: v.recipientContactName.trim(),
					DeliveryRecipientPhone: recipientPhone,
					DeliverySenderName: v.senderContactName.trim(),
					DeliverySenderPhone: senderPhone,
					DeliveryWeight: v.weightMaxKg,
					DeliveryItemTypeId: v.deliveryItemType,
					DeliverySize: v.parcelSize,
					PackagePhotoBase64: packagePhotoDataUrl,
				},
				ServiceTypeId: XpressServiceType.Delivery,
			};

			await axiosInstance.post(
				ENDPOINTS['/api/X4bActivity/PlaceOrder'],
				body,
			);
		},
		onSuccess: () => {
			showNotification('Delivery booking has been requested');
			navigate(routeWithId('/concierge/statuses/:id', 0));
		},
	});

	return (
		<Column
			className={cn(
				'w-full h-screen flex flex-col new-send-booking-text-compact',
			)}
		>
			<ConciergeHeader marginClass="mx-6 mt-4" />

			{step === 3 ? (
				<div className="new-send-booking-summary-screen flex flex-1 min-h-0 w-full flex-col items-center overflow-y-auto py-6 px-3">
					<NewSendBookingStep3
						values={values}
						pickupLabel={pickupLabel}
						dropoffLabel={dropoffLabel}
						googleMapsRoute={googleMapsRoute}
						parcelPhotoPreviewUrl={parcelPhotoPreviewUrl}
						senderDialCode={senderCountry.dialCode}
						recipientDialCode={recipientCountry.dialCode}
						routePricingData={routePricingData}
						selectedPricingVehicle={selectedDeliveryPricingVehicle}
						onEditDetails={() => setStep(2)}
						confirmBookingPending={placeOrderMutation.isPending}
						onConfirmBooking={() => placeOrderMutation.mutate()}
					/>
				</div>
			) : (
				<Row className="new-send-booking-shell new-send-booking-sidebar-h x4b-concierge w-full flex-1 min-h-0 p-6 gap-6">
					<Column className="new-send-booking-sidebar h-full w-[min(420px,50vw)] shrink-0 min-w-[300px] rounded-[26px] bg-white blue-scroll flex flex-col">
					<Row className="new-send-booking-sidebar-header p-6 items-center justify-between border-b border-[#CCDFE5] shrink-0">
						<TextHeadlineMedium
							text="New Send Booking"
							className="text-primary"
						/>
						<button
							type="button"
							onClick={() =>
								navigate(
									routeWithId('/concierge/statuses/:id', 0),
								)
							}
							className="new-send-booking-close-btn rounded-full p-1 transition duration-200 hover:bg-gray-200"
							aria-label="Close"
						>
							<MdClose size={20} className="text-black" />
						</button>
					</Row>

					<Column className="new-send-booking-sidebar-meta px-6 pt-3 shrink-0 gap-1">
						<TextBodySmall
							className="text-onSurfaceVariant"
							text={
								step === 1
									? 'Step 1 of 3 · Sender & recipient'
									: 'Step 2 of 3 · Parcel & payer'
							}
						/>
						{step === 1 && routeGeoReady && routePricingLoading ? (
							<Row className="gap-2 items-center" aria-live="polite">
								<Spinner size="inline" />
								<TextBodySmall
									className="text-primary60"
									text="Updating route on map…"
								/>
							</Row>
						) : null}
						{step === 1 && routeGeoReady && routePricingIsError ? (
							<div role="alert" aria-live="polite">
								<TextBodySmall
									className="text-error60"
									text={routePricingQueryErrorMessage}
								/>
							</div>
						) : null}
					</Column>

					<Column className="new-send-booking-sidebar-scroll gap-8 flex-1 min-h-0 overflow-y-auto blue-scroll px-6 pr-4 mr-2 py-4">
						{step === 1 && (
							<Column className="new-send-booking-step1-stack gap-8">
								<Column className="new-send-booking-step1-section gap-4">
									<SectionHeading
										title="Sender Information"
										required
									/>
									<InputController
										id="senderContactName"
										control={form.control}
									>
										<TextInput
											variant="outlined"
											label="Contact name"
											placeholder="Full name"
										/>
									</InputController>
									<Row className="new-send-booking-phone-row gap-2">
										<SelectCountryIconDropdownConcierge
											value={senderCountry}
											onChange={(
												e: DropdownChangeEvent,
											) => {
												form.resetField('senderPhone');
												setSenderCountry(e.value);
												form.setValue(
													'senderMobileNumberPrefix',
													e.value.code,
													{ shouldValidate: true },
												);
											}}
										/>
										<InputController
											id="senderPhone"
											control={form.control}
										>
											<TextInput
												variant="outlined"
												label="Phone number"
												placeholder="000 0000 000"
												keyfilter="num"
											/>
										</InputController>
									</Row>
									<AddressDropdownInput<NewSendBookingFormValues>
										id="pickUpAddress"
										label="Pickup address"
										formName={formId}
										control={form.control}
										pointType={X4bPointType.Pickup}
									/>
								</Column>

								<div
									className="bg-neutralVariant30 h-px shrink-0 w-full"
									aria-hidden
								/>

								<Column className="new-send-booking-step1-section gap-4">
									<SectionHeading
										title="Recipient Information"
										required
									/>
									<InputController
										id="recipientContactName"
										control={form.control}
									>
										<TextInput
											variant="outlined"
											label="Contact name"
											placeholder="Full name"
										/>
									</InputController>
									<Row className="new-send-booking-phone-row gap-2">
										<SelectCountryIconDropdownConcierge
											value={recipientCountry}
											onChange={(
												e: DropdownChangeEvent,
											) => {
												form.resetField(
													'recipientPhone',
												);
												setRecipientCountry(e.value);
												form.setValue(
													'recipientMobileNumberPrefix',
													e.value.code,
													{ shouldValidate: true },
												);
											}}
										/>
										<InputController
											id="recipientPhone"
											control={form.control}
										>
											<TextInput
												variant="outlined"
												label="Phone number"
												placeholder="000 0000 000"
												keyfilter="num"
											/>
										</InputController>
									</Row>
									<AddressDropdownInput<NewSendBookingFormValues>
										id="dropOffAddress"
										label="Dropoff address"
										formName={formId}
										control={form.control}
										pointType={X4bPointType.Dropoff}
									/>
								</Column>
							</Column>
						)}

						{step === 2 && (
							<NewSendBookingStep2
								control={form.control}
								senderSubtitle={senderSubtitle}
								recipientSubtitle={recipientSubtitle}
								parcelPhoto={parcelPhoto}
								parcelPhotoPreviewUrl={parcelPhotoPreviewUrl}
								onParcelPhotoChange={setParcelPhoto}
								errors={step2Errors}
								routeGeoReady={routeGeoReady}
								vehicleTypesForPricing={
									routePricingData?.VehicleTypes
								}
								routePricingLoading={routePricingLoading}
								routePricingIsError={routePricingIsError}
								routePricingErrorMessage={
									routePricingQueryErrorMessage
								}
								deliveryVehicleTypeId={deliveryVehicleTypeId}
								onDeliveryVehicleTypeIdChange={
									setDeliveryVehicleTypeId
								}
							/>
						)}

					</Column>

					<Row className="new-send-booking-footer new-send-booking-footer-row h-20 px-6 py-3 gap-2 justify-end border-t border-[#CCDFE5] shrink-0 items-center">
						{step > 1 && (
							<Button
								type="button"
								label="Back"
								icon={<MdArrowBack className="text-white" />}
								onClick={() => {
									setStep((s) => {
										const next = (s === 3 ? 2 : 1) as
											| 1
											| 2
											| 3;
										if (s === 2 && next === 1) {
											setStep2Errors({});
										}
										return next;
									});
								}}
								pt={{
									root: {
										className:
											'px-4 py-2.5 rounded-[100px] bg-primary60 border-primary60',
									},
									label: {
										className:
											'font-poppins text-label-large2 text-white',
									},
								}}
							/>
						)}
						{step === 2 && (
							<Button
								type="button"
								label="Review Summary"
								icon={<MdArrowForward className="text-white" />}
								iconPos="right"
								disabled={!step2Complete}
								onClick={() => {
									goNextFromStep2();
								}}
								pt={{
									root: {
										className:
											'px-5 py-2.5 rounded-[100px] gap-1.5 bg-primary60',
									},
									label: {
										className:
											'font-poppins text-label-large2 text-white',
									},
								}}
							/>
						)}
						{step === 1 && (
							<Button
								type="button"
								label="Next"
								icon={<MdArrowForward className="text-white" />}
								iconPos="right"
								disabled={step1NextDisabled}
								onClick={() => void goNextFromStep1()}
								pt={{
									root: {
										className: cn(
											'px-5 py-2.5 rounded-[100px] gap-1.5 bg-primary60',
											step1NextDisabled
												? 'opacity-50 pointer-events-none'
												: '',
										),
									},
									label: {
										className:
											'font-poppins text-label-large2 text-white',
									},
								}}
							/>
						)}
					</Row>
				</Column>

					<Container className="overflow-hidden h-full grow min-w-0 rounded-[22px]">
						<ConciergeLiveMap
							pickupLocationName={pickupLabel}
							dropOffLocationName={dropoffLabel}
							initialRoute={googleMapsRoute}
							activity={undefined}
						/>
					</Container>
				</Row>
			)}
		</Column>
	);
};

export default NewSendBooking;
